import '../css/app.css';
import 'vendors/lazysizes';

import debounce from 'lodash/debounce';
import { picoapp } from 'picoapp';

import { getCart } from 'utils/cart';
import { dispatch } from 'utils/theme';

import 'layout/homepage';

import accordion from './components/accordion';
import banner from './components/banner';
// Components
import buttonBack from './components/button-back';
import buttonCopyUrl from './components/button-copy-url';
import carousel from './components/carousel';
// Cart components
import {
  cartCheckout,
  cartContainer,
  cartDiscountProgressBar,
  cartItem,
  cartItemList,
  cartSummary,
  cartUpsell,
  cartUsps,
  quantitySelector,
  recommendationsSlider,
} from './components/cart';
import cartCounter from './components/cart-counter';
import chatButton from './components/chat-button';
import collapsible from './components/collapsible';
import combinationForm from './components/combination-form';
import contactForm from './components/contact-form';
import copyForm from './components/copy-form';
import copyPasteButton from './components/copy-paste-button';
import countdown from './components/countdown';
import faq from './components/faq';
import infoBox from './components/info-box';
import mediaBanners from './components/media-banners';
import mediaScroll from './components/media-scroll';
import modals from './components/modals';
// Product form components
import {
  productAddToCart,
  productBackInStockForm,
  productFitOptions,
  productForm,
  productFormMerger,
  productFormWrapper,
  productSizeOptions,
  productVariantSelect,
} from './components/product';
import productFormOld from './components/product-form';
import productInfoModalContent from './components/product-info-modal-content';
import productItem from './components/product-item';
import productPopupsBanner from './components/product-popups-banner';
import qatcModalContent from './components/qatc-modal-content';
import quantity from './components/quantity';
import regionSwitch from './components/region-switch';
import rte from './components/rte';
import shareBox from './components/share-box';
import simpleVideo from './components/simple-video';
import singleVideo from './components/single-video';
import splitDrag from './components/split-drag';
import stlInfoModal from './components/stl-info-modal';
import video from './components/video';
import videoAutoplay from './components/video-autoplay';
import wishlistCounter from './components/wishlist-counter';
import wishlistLookToggle from './components/wishlist-look-toggle';
import wishlistToggle from './components/wishlist-toggle';
import {
  CART_LOADING,
  COUNTDOWN_FINISHED,
  COUNTDOWN_UPDATE,
  DOCUMENT_CLICK,
  INIT_MOVEABLE_CONTENT,
  MODAL_OPEN,
  MOUNT_COMPONENTS,
  REGISTER_COUNTDOWN,
  THEME_CART_UPDATED,
  UNREGISTER_COUNTDOWN,
  UPDATE_VISIBLE_SCREEN_HEIGHT,
} from './events';
// Layout
import announcement from './layout/announcement';
import header from './layout/header/main';
import headerNavigation from './layout/header/navigation';
import navigationDrawer from './layout/navigation-drawer';
import searchModal from './layout/search-modal';
import Tracking from './lib/tracking';
import Wishlist from './lib/wishlist';
import initMoveableContent from './vendors/moveable-content';
import Captcha from './vendors/recaptcha';
// Vendors
import './vendors/usercentrics';
import './vendors/lazy-package-loader';
import './vendors/sections-observer';

import { App } from '@grafikr/components';

import components from 'framework/components';
import state from 'framework/state';

const v2App = new App();

state(v2App);
components(v2App);
v2App.mount();

document.addEventListener('shopify:section:load', ({ detail }) => {
  const section = document.getElementById(`shopify-section-${detail.sectionId}`);

  if (section) {
    v2App.mount(section);
  }
});

const app = picoapp(
  {
    // Layout
    announcement,
    header,
    'header-navigation': headerNavigation,
    'navigation-drawer': navigationDrawer,
    'search-modal': searchModal,

    // Components
    'button-back': buttonBack,
    'button-copy-url': buttonCopyUrl,
    'cart-counter': cartCounter,
    collapsible,
    faq,
    'copy-form': copyForm,
    'media-scroll': mediaScroll,
    banner,
    carousel,
    quantity,
    'region-switch': regionSwitch,
    rte,
    'single-video': singleVideo,
    'split-drag': splitDrag,
    'product-form': productFormOld,
    'product-item': productItem,
    video,
    'video-autoplay': videoAutoplay,
    'wishlist-toggle': wishlistToggle,
    'wishlist-look-toggle': wishlistLookToggle,
    'wishlist-counter': wishlistCounter,
    'chat-button': chatButton,
    'contact-form': contactForm,
    countdown,
    modals,
    'stl-info-modal': stlInfoModal,
    'product-info-modal-content': productInfoModalContent,
    'info-box': infoBox,
    'share-box': shareBox,
    accordion,
    'simple-video': simpleVideo,
    'combination-form': combinationForm,
    'qatc-modal-content': qatcModalContent,
    'product-popups-banner': productPopupsBanner,
    'media-banners': mediaBanners,

    // Product form components
    'product.form': productForm,
    'product.add-to-cart': productAddToCart,
    'product.size-options': productSizeOptions,
    'product.fit-options': productFitOptions,
    'product.form-wrapper': productFormWrapper,
    'product.form-merger': productFormMerger,
    'product.variant-select': productVariantSelect,
    'product.back-in-stock-form': productBackInStockForm,

    // Cart components
    'cart.container': cartContainer,
    'cart.checkout': cartCheckout,
    'cart.discount-progress-bar': cartDiscountProgressBar,
    'cart.item': cartItem,
    'cart.item-list': cartItemList,
    'cart.summary': cartSummary,
    'cart.usps': cartUsps,
    'cart.quantity-selector': quantitySelector,
    'cart.recommendations-slider': recommendationsSlider,
    'cart.upsell': cartUpsell,
    'copy-paste-button': copyPasteButton,
  },
  {
    wishlist: Wishlist.items,
    // Store form states under product ID's
    forms: {},
    activeModals: [],
    variantsInventory: { ...window.theme.cart.stock_data },
    variantsPrices: { ...window.theme.cart.price_data },
    cart: window.theme.cart.initialCart || {},
    search: {
      results: {},
      query: new URLSearchParams(window.location.search).get('q') || '',
    },
  },
);

if (!Shopify.Checkout) {
  Tracking.startup();
  Captcha.startup();
}

app.on('init', () => app.mount());
app.on(MOUNT_COMPONENTS, () => app.mount());
app.on('unmount:components', () => app.unmount());
app.on(INIT_MOVEABLE_CONTENT, initMoveableContent);
document.addEventListener('app:mount', () => app.mount());
document.addEventListener('app:unmount', () => app.unmount());

if (window.Shopify.designMode)
  document.addEventListener('shopify:section:load', () => {
    app.unmount();
    app.mount();
  });

let counterDates = [];
let counterInterval;

const getCountdownData = (date) => {
  const end = new Date(date);
  const now = new Date();
  const distance = end - now;

  return {
    days: distance > 0 ? Math.floor(distance / (1000 * 60 * 60 * 24)) : 0,
    hours: distance > 0 ? Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)) : 0,
    minutes: distance > 0 ? Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)) : 0,
    seconds: distance > 0 ? Math.floor((distance % (1000 * 60)) / 1000) : 0,
  };
};

const updateCounters = () => {
  const countersValues = counterDates.map((date) => ({ date, timeLeft: getCountdownData(date) }));

  countersValues.forEach(({ date, timeLeft }) => {
    app.emit(COUNTDOWN_UPDATE(date), null, timeLeft);

    // Counter has finished
    if (Math.max(...Object.values(timeLeft)) === 0) {
      app.emit(COUNTDOWN_FINISHED(date));
      app.emit(UNREGISTER_COUNTDOWN, null, date);
    }
  });
};

const initCounterInterval = () => {
  counterInterval = setInterval(updateCounters, 1000);
};

app.on(UNREGISTER_COUNTDOWN, (state, date) => {
  counterDates = counterDates.filter((d) => d !== date);

  if (counterDates.length === 0) clearInterval(counterInterval);
});

app.on(REGISTER_COUNTDOWN, (state, date) => {
  if (counterDates.length === 0) initCounterInterval();

  counterDates = [...new Set([...counterDates, date])];
});

// Add some global component that handles all add to cart events
document.addEventListener('product:form:submit', async (e) => {
  const { formId, form, formData, product, variant, items, placement } = e.detail;
  const productId = formData.get('product-id');
  const algoliaRecentSearch = JSON.parse(localStorage.getItem('algolia_recent_product') || '{}');

  document.dispatchEvent(new CustomEvent(`product:${formId}:loading`, { bubbles: false }));

  app.emit(CART_LOADING);

  if (`${algoliaRecentSearch?.id}` === `${productId}`) {
    formData.append('properties[_algoliaSearch]', algoliaRecentSearch.queryID);
  }

  await fetch(`${form.action}.js`, {
    method: form.method,
    body: formData,
  });

  // Some bug with scroll lock
  app.emit(MODAL_OPEN, null, { id: 'cart', props: { action: 'add_to_cart' } });

  await getCart().then((response) => {
    document.dispatchEvent(new CustomEvent(`product:${formId}:loading-done`, { bubbles: false }));

    if (response.status === 422) {
      return;
    }

    dispatch('add-to-cart', {
      product,
      variant,
      quantity: parseInt(formData.get('quantity') ?? 1, 10),
      items,
      placement,
    });

    app.emit(THEME_CART_UPDATED, { cart: response });
    dispatch('view-cart', { cart: response });
    localStorage.setItem(
      'lineItemsProperties',
      JSON.stringify(response.items.map((item) => item.properties)),
    );
  });
});

document.addEventListener('click', (event) => {
  app.emit(DOCUMENT_CLICK, null, { event });
});

const updateVisibleScreenHeight = debounce(() => {
  const screenHeightRuler = document.querySelector('[data-visible-screenheight-ruler]');
  document.body.style.setProperty('--visible-screenheight', `${screenHeightRuler.offsetHeight}px`);
}, 200);

const updateThemeObject = debounce(() => {
  const { isMobile } = window.theme;

  window.theme.isMobile = window.innerWidth < 1024;
  document.dispatchEvent(new CustomEvent('theme:screen:size-updated'));

  if (isMobile !== window.theme.isMobile)
    document.dispatchEvent(new CustomEvent('theme:screen:is-mobile-updated'));
}, 200);

window.addEventListener('resize', () => {
  updateThemeObject();
  updateVisibleScreenHeight();
});

window.addEventListener('DOMContentLoaded', () => {
  updateThemeObject();
});

app.on(UPDATE_VISIBLE_SCREEN_HEIGHT, updateVisibleScreenHeight);
document.addEventListener('update-visible-screen-heigh', updateVisibleScreenHeight);

updateVisibleScreenHeight();

export default app;
